import { useState, useCallback } from 'react';
import { TimeSlot } from '../../../types/booking';
import bookingService, { CreateBookingDto } from '../../../services/bookingService';
import { MeetingType } from '../../../services/meetingTypeService';
import { FormTemplate } from '../../../services/formTemplateService';
import { extractSystemFields } from '../../../utils/formFieldExtractor';
import { clearPurchaseGrant } from '../../../services/customerService';
import { PublicProduct, PublicProvider } from '../../../types/product';

interface BookingFormData {
  name?: string;
  email?: string;
  notes?: string;
  [key: string]: any;
}

interface EnhancedError {
  message: string;
  errorCode?: string;
  shouldRefreshCache?: boolean;
  status?: number;
}

interface UseBookingSubmissionProps {
  meetingType: MeetingType | null;
  selectedTime: TimeSlot | null;
  selectedTimezone: string;
  token: string | null;
  selectedDate: Date;
  hostId?: string;
  mode?: 'booking' | 'reschedule' | 'preview';
  rescheduleToken?: string;
  formTemplate?: FormTemplate | null;
  onBookingSuccess?: (booking: any) => void;
  onBookingError?: (error: string, enhancedError?: EnhancedError) => void;
  /** Service booking selection (for service type meetings) */
  serviceSelection?: {
    service: PublicProduct | null; // Can be null in duration mode (no service options)
    provider: PublicProvider | null;
    isNoPreference: boolean;
    effectiveDuration: number; // Duration from service or meeting type
    effectivePrice: number;   // Total price (service + provider fee)
  } | null;
  /** Event waitlist: claim token from a "spot held for you" email link.
   *  Lets the guest book a slot that is publicly full. */
  waitlistClaimToken?: string | null;
}

interface UseBookingSubmissionResult {
  isSubmitting: boolean;
  bookingSuccess: boolean;
  bookingDetails: any;
  error: string | null;
  submitBooking: (formData: BookingFormData) => Promise<void>;
  /** Clear the error state (e.g., when a parent handles the error via banner instead of inline) */
  clearError: () => void;
  /**
   * Phase 3 — set when the server requires payment before the booking is
   * confirmed. The parent should route to PaymentScreen instead of the
   * normal confirmation flow.
   */
  paymentInfo: {
    bookingId: number;
    clientSecret: string;
    publishableKey: string;
    connectedAccountId: string;
    expiresAt: string;
    amount: number;
    currency: string;
  } | null;
  /**
   * Set when the chosen option is a PLAN this person is not on yet.
   *
   * Not an error — it is the last step of the booking. They picked the plan
   * option, so subscribing is the thing they came to do; the parent renders the
   * plan checkout and, once it succeeds, calls `submitBooking` again with the
   * same form data. Refusing them instead is what made a plan a locked door.
   */
  planPurchase: {
    productId: number;
    /** The HELD booking. Confirmed after the plan goes live — never re-created. */
    bookingId: number;
    /** When the hold lapses, so the screen can count down honestly. */
    expiresAt: string;
  } | null;
  /** Dismiss the plan checkout, e.g. the invitee backed out of subscribing. */
  clearPlanPurchase: () => void;
}

/**
 * Hook to handle booking submission
 */
export const useBookingSubmission = ({
  meetingType,
  selectedTime,
  selectedTimezone,
  token,
  selectedDate,
  hostId,
  mode = 'booking',
  rescheduleToken,
  formTemplate,
  onBookingSuccess,
  onBookingError,
  serviceSelection,
  waitlistClaimToken,
}: UseBookingSubmissionProps): UseBookingSubmissionResult => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [bookingSuccess, setBookingSuccess] = useState<boolean>(false);
  const [bookingDetails, setBookingDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<UseBookingSubmissionResult['paymentInfo']>(null);
  const [planPurchase, setPlanPurchase] =
    useState<UseBookingSubmissionResult['planPurchase']>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearPlanPurchase = useCallback(() => {
    setPlanPurchase(null);
  }, []);

  const submitBooking = useCallback(async (formData: BookingFormData) => {
    // Reset state
    setIsSubmitting(true);
    setError(null);
    setBookingSuccess(false);
    setBookingDetails(null);
    // Cleared on every attempt: this is a step to pass through, not a state to
    // linger in. A stale one would re-open the checkout over a booking that has
    // since gone through.
    setPlanPurchase(null);

    // Validate meeting type and selected time
    if (!meetingType || !selectedDate || !selectedTime) {
      const errorMessage = 'Missing required booking information';
      setError(errorMessage);
      setIsSubmitting(false);

      if (onBookingError) {
        onBookingError(errorMessage);
      }
      return;
    }

    try {
      // Extract system fields from form data using the form template
      // This allows us to work with ANY custom form structure
      const systemFields = extractSystemFields(formData, formTemplate || null);

      // Validate that we have the required fields (firstName, lastName, and email)
      if (!systemFields.firstName || !systemFields.firstName.trim()) {
        const errorMessage = 'First name is required for booking';
        setError(errorMessage);
        setIsSubmitting(false);
        if (onBookingError) {
          onBookingError(errorMessage);
        }
        return;
      }

      if (!systemFields.lastName || !systemFields.lastName.trim()) {
        const errorMessage = 'Last name is required for booking';
        setError(errorMessage);
        setIsSubmitting(false);
        if (onBookingError) {
          onBookingError(errorMessage);
        }
        return;
      }

      if (!systemFields.email || !systemFields.email.trim()) {
        const errorMessage = 'Email is required for booking';
        setError(errorMessage);
        setIsSubmitting(false);
        if (onBookingError) {
          onBookingError(errorMessage);
        }
        return;
      }

      // PREVIEW MODE: Generate mock booking data without API call
      if (mode === 'preview') {
        // Simulate a short delay for realistic UX
        await new Promise(resolve => setTimeout(resolve, 800));

        // Generate mock booking data
        const mockBooking = {
          id: 'preview-' + Date.now(),
          meetingTypeId: meetingType.id,
          guestName: systemFields.name,
          guestEmail: systemFields.email,
          guestPhone: systemFields.phone || '',
          startTime: selectedTime.start.toISOString(),
          endTime: selectedTime.end.toISOString(),
          timeZone: selectedTimezone,
          notes: systemFields.notes || '',
          status: 'confirmed',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          // Include location for in-person/phone meetings
          location: meetingType.isInPerson
            ? (meetingType.locationDetails || 'In-person meeting location')
            : (!meetingType.isVirtual && meetingType.phoneNumber
              ? `${meetingType.countryCode || '+1'} ${meetingType.phoneNumber}`
              : ''),
          host: {
            id: meetingType.userId,
            name: meetingType.user?.name || 'Host Name',
            email: meetingType.user?.email || 'host@example.com',
          },
          // Mock video conference data if meeting is virtual
          ...(meetingType.isVirtual !== false && {
            meeting: {
              provider: 'zoom',
              joinUrl: 'https://www.yourzoomlink.example/meeting/preview',
              password: null,
            },
          }),
        };

        const formattedBookingDetails = {
          booking: mockBooking,
          meetingTitle: meetingType?.title || 'Meeting',
          hostName: meetingType.user?.name || 'Host Name',
          timezone: selectedTimezone,
        };

        // Set success states
        setBookingSuccess(true);
        setBookingDetails(formattedBookingDetails);

        if (onBookingSuccess) {
          onBookingSuccess(mockBooking);
        }

        setIsSubmitting(false);
        return;
      }

      // Create the booking data with only the fields the server expects
      // Server expects "local" time in the selected timezone, so we need to extract the
      // time components in the target timezone and send them as if they were local

      // The server expects to receive a time that represents LOCAL time in the target timezone
      // but the server receives UTC and incorrectly assumes it's local time
      //
      // Current problem:
      // - User selects 11:00 AM Tokyo (which is 2:00 AM UTC)
      // - Server receives "2025-08-25T02:00:00.000Z"
      // - Server thinks this is "2:00 AM local Tokyo time"
      // - Server converts "2:00 AM Tokyo" to UTC = "2025-08-24T17:00:00.000Z"
      //
      // Solution: Send time that LOOKS like "11:00 AM" when converted to UTC
      // So server will think "11:00 AM local Tokyo" and convert correctly

      // We want server to receive "2025-08-25T11:00:00.000Z" so it thinks "11:00 AM local"
      // Extract time components
      const startInTargetTz = selectedTime.start.toLocaleString("sv-SE", { timeZone: selectedTimezone });
      const endInTargetTz = selectedTime.end.toLocaleString("sv-SE", { timeZone: selectedTimezone });

      // Convert to ISO format that represents the local time as if it were UTC
      const localStartDate = new Date(`${startInTargetTz}Z`); // Add Z to make it UTC
      const localEndDate = new Date(`${endInTargetTz}Z`);

      // Compute the actual booking duration from the selected time slot (in minutes)
      // This is the source of truth — it matches what the user sees on screen
      const bookingDurationMinutes = Math.round(
        (localEndDate.getTime() - localStartDate.getTime()) / 60000
      );

      const bookingData = {
        meetingTypeId: String(meetingType.id),
        name: systemFields.name, // Extracted from form using systemFieldKey
        email: systemFields.email, // Extracted from form using systemFieldKey
        phone: systemFields.phone || '', // Extracted from form (optional)
        startTime: localStartDate, // Send as "local" time for server conversion
        endTime: localEndDate,     // Send as "local" time for server conversion
        duration: bookingDurationMinutes, // Always send duration so the server can validate it
        timeZone: selectedTimezone,
        notes: systemFields.notes || '', // Extracted from form (optional)
        formSubmissionData: formData, // Send all form data for FormSubmission table
        // Event waitlist claim — lets this booking take the spot held for the guest
        ...(waitlistClaimToken ? { waitlistClaimToken } : {}),
        // Service booking data (for service type meetings)
        // Two modes: 'options' (service selection) and 'duration' (no service, just provider)
        ...(serviceSelection && {
          // Always include provider data for service bookings
          selectedProviderId: serviceSelection.provider?.userId || null,
          providerFeeSnapshot: serviceSelection.provider?.additionalFee || 0,
          // Service options mode: include service option ID and price
          ...(serviceSelection.service
            ? {
                productId: serviceSelection.service.id,
                servicePriceSnapshot: serviceSelection.service.price || 0,
              }
            : {}),
        }),
      };

      // Call appropriate service method based on mode
      let booking;
      if (mode === 'reschedule' && rescheduleToken) {
        booking = await bookingService.rescheduleBooking(rescheduleToken, {
          ...bookingData,
          token: token // Keep token for any auth needs
        });
      } else {
        // Call booking service with the data and token separately
        booking = await bookingService.createBooking({
          ...bookingData,
          token: token // Add token back for the service to handle in headers
        });
      }

      // Store a properly formatted booking detail object for the success page
      const formattedBookingDetails = {
        booking: {
          ...booking,
          host: booking.host || {
            name: meetingType.user?.name || "Host",
            email: meetingType.user?.email
          }
        },
        meetingTitle: meetingType?.title || "Meeting",
        hostName: booking.host?.name || meetingType.user?.name || "Host",
        timezone: selectedTimezone,
        // What they actually booked, so the confirmation can name it.
        bookedOptionName: serviceSelection?.service?.title || null,
        /**
         * FALSE on this path, always.
         *
         * "Your plan is active" announces something that just happened. Reaching
         * here means the plan was ALREADY theirs — nothing was activated, and
         * telling a long-standing subscriber their plan is now active reads as
         * though they had just been charged. Only the checkout path, which
         * really did start one, says it.
         */
        bookedOptionIsPlan: false,
        // Pass video integration status from API response for pending/error handling
        videoIntegrationStatus: booking._videoIntegrationStatus || null,
        videoIntegrationError: booking._videoIntegrationError || null,
      };

      /**
       * The booking is HELD while they subscribe to the plan that unlocks it.
       *
       * It already exists and the slot is reserved, so the checkout confirms
       * THIS booking rather than submitting a second one — which is what the
       * old re-submit did, in a window where the slot could be taken.
       */
      if (booking._planPurchaseRequired && booking._planPurchase) {
        setPlanPurchase({
          productId: Number(booking._planPurchase.productId),
          bookingId: Number(booking._planPurchase.bookingId),
          expiresAt: booking._planPurchase.expiresAt,
        });
        setIsSubmitting(false);
        return;
      }

      // Phase 3 — Pay Now branch. The booking row exists in `pending` state
      // server-side, but post-booking actions (calendar, emails) are deferred
      // until the invitee completes Stripe payment. Surface paymentInfo so
      // the parent can render PaymentScreen instead of the confirmation page.
      if (booking._paymentRequired && booking._payment) {
        setBookingDetails(formattedBookingDetails);
        setPaymentInfo({
          bookingId: booking.id,
          clientSecret: booking._payment.clientSecret,
          publishableKey: booking._payment.publishableKey,
          connectedAccountId: booking._payment.connectedAccountId,
          expiresAt: booking._payment.expiresAt,
          amount: booking._payment.amount,
          currency: booking._payment.currency,
        });
        setIsSubmitting(false);
        return;
      }

      /**
       * The receipt has done its job.
       *
       * It exists to carry one purchase into the booking that prompted it, and
       * that booking now exists. Leaving it in the tab would let a second
       * booking spend against the same wallet without the buyer being anybody
       * — which the ledger would refuse anyway, but as a confusing failure
       * rather than an honest prompt to sign in.
       */
      clearPurchaseGrant();

      // Immediately set success states after receiving a response
      setBookingSuccess(true);
      setBookingDetails(formattedBookingDetails);

      // Trigger any callbacks
      if (onBookingSuccess) {
        onBookingSuccess(booking);
      }
    } catch (error: any) {
      // Extract error information
      const errorCode = error.errorCode;
      const shouldRefreshCache = error.shouldRefreshCache;
      let errorMessage = error.message || `Failed to ${mode === 'reschedule' ? 'reschedule' : 'create'} booking`;

      // Parse error message to extract error code if prefixed
      if (errorMessage.includes(':')) {
        const parts = errorMessage.split(':');
        const possibleCode = parts[0].trim();
        if (['TIMEOUT', 'NETWORK_ERROR', 'SLOT_UNAVAILABLE'].includes(possibleCode)) {
          errorMessage = parts.slice(1).join(':').trim();
        }
      }

      // Store error with metadata
      const enhancedError = {
        message: errorMessage,
        errorCode: errorCode,
        shouldRefreshCache: shouldRefreshCache,
        status: error.status
      };

      setError(errorMessage);

      if (onBookingError) {
        // Pass enhanced error object if callback expects it
        onBookingError(errorMessage, enhancedError);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    meetingType,
    selectedTime,
    selectedDate,
    selectedTimezone,
    token,
    hostId,
    onBookingSuccess,
    onBookingError,
    serviceSelection,
    waitlistClaimToken,
  ]);

  return {
    isSubmitting,
    bookingSuccess,
    bookingDetails,
    error,
    submitBooking,
    clearError,
    paymentInfo,
    planPurchase,
    clearPlanPurchase,
  };
}; 